import { captureError } from './errors'
import { AppError, normalizeError, stackFromAny, type AppErrorContext, type AppErrorKind } from './appError'

// Local, short TTL dedup for handled UI errors (additional to captureError dedup).
const seen: Map<string, number> = new Map()
const TTL_MS = 15_000

function isDup(key: string): boolean {
  const now = Date.now()
  const last = seen.get(key) || 0
  if (now - last < TTL_MS) return true
  seen.set(key, now)
  if (seen.size > 500) {
    const cutoff = now - TTL_MS
    for (const [k, t] of seen.entries()) {
      if (t < cutoff) seen.delete(k)
      if (seen.size <= 300) break
    }
  }
  return false
}

/**
 * reportHandledError:
 * - для "handled" ошибок, которые превращаются в UI err state (`setErr(...)`)
 * - автоматически репортит в telemetry с стабильным fingerprint (`kind/code`)
 * - возвращает строку для UI (friendly)
 */
export function reportHandledError(input: {
  err: any
  kind?: AppErrorKind
  code: string
  userMessage: string
  context?: AppErrorContext
  severity?: 'warning' | 'error'
}): string {
  try {
    const extraCtx =
      (input?.err && typeof input.err === 'object' && (input.err as any).telemetry && typeof (input.err as any).telemetry === 'object')
        ? { ...(input.err as any).telemetry }
        : null
    const mergedCtx: AppErrorContext = { ...(input.context || {}), ...(extraCtx || {}) }

    // Important:
    // - `input.code` is the stable UI-level error code (feature-level).
    // - If `input.err` is already an AppError, we keep it as root-cause but DO NOT use its code
    //   for grouping (otherwise one UI problem splinters into many low-level codes like http_500/http_502).
    const root = (input.err instanceof AppError) ? input.err : null
    const kind: AppErrorKind = (input.kind || root?.kind || 'unknown') as any
    const uiErr: AppError =
      root
        ? new AppError({
          kind,
          code: input.code,
          message: String(root.message || input.code),
          status: root.status,
          context: {
            ...(root.context || {}),
            ...(mergedCtx || {}),
            root_kind: root.kind,
            root_code: root.code,
            root_status: root.status,
          },
          cause: root,
        })
        : normalizeError(input.err, { kind, code: input.code, context: mergedCtx || {} })

    const key = `ui|${uiErr.kind}|${uiErr.code}`
    if (!isDup(key)) {
      captureError({
        type: 'ui_error',
        // stable message for grouping; details go to meta
        message: `${uiErr.kind}:${uiErr.code}`,
        stack: stackFromAny(input.err) || stackFromAny(uiErr.cause),
        severity: input.severity || 'error',
        meta: {
          kind: uiErr.kind,
          code: uiErr.code,
          status: uiErr.status,
          message: String(uiErr.message || ''),
          ...(uiErr.context || {}),
        },
      })
    }
  } catch {
    // never throw from monitoring
  }
  return String(input.userMessage || 'Something went wrong')
}

